import { authApi } from '@/api/auth';
import { useGet } from '@foodics/ui-common';
import { computed, watch } from 'vue';
import type { WhoAmIApiRequest } from '@/types/api/auth';
import { useAuth } from './auth';
import { createGlobalState } from '@vueuse/core';
import type { Permission } from '@/types/models';
import { onUpdateMember } from './teamMembers';
import { merge } from 'lodash-es';

export const useCurrentUser = createGlobalState(() => {
    const {
        data,
        execute: executeWhoAmI,
        onData: onCurrentUserLoaded,
    } = useGet<{
        data: WhoAmIApiRequest['response'];
    }>(authApi.whoAmI, { disableAutoExecute: true });

    const { isAuthenticated } = useAuth();

    watch(
        isAuthenticated,
        (newValue) => {
            if (newValue) {
                executeWhoAmI();
            }
        },
        { immediate: true },
    );

    const usersPermissions = computed(
        () =>
            data.value?.data?.roles?.flatMap((role) => role.permissions) ?? [],
    );

    const permissionsSet = computed(
        () => new Set(usersPermissions.value.map((p) => p.name)),
    );

    const hasPermissions = (
        permission: Permission['name'] | Permission['name'][],
        match: 'all' | 'any' = 'all',
    ) => {
        const permissionList = Array.isArray(permission)
            ? permission
            : [permission];

        if (permissionsSet.value.size === 0) return false;

        return match === 'all'
            ? permissionList.every((perm) => permissionsSet.value.has(perm))
            : permissionList.some((perm) => permissionsSet.value.has(perm));
    };

    onUpdateMember((member) => {
        if (data.value?.data.id !== member.id) return;

        if (data.value?.data) {
            data.value.data = merge({}, data.value.data, member);
        }
    });

    return {
        user: computed(() => data.value?.data),
        hasPermissions,
        onCurrentUserLoaded,
    };
});
